import type { Alert, SourceStatus, WeatherReport } from '../../../shared/contract'

/** Client-side structural seam for AlertsReader's result. No edge/runtime imports. */
export interface AlertsFeed {
  alerts: readonly AlertMessage[]
  freshness: { as_of: string; expires_at: string; status: 'available' | 'unavailable' | 'unsupported' }
  sources: readonly AlertSource[]
}
export interface AlertSource {
  id: string
  kind: string
  url: string
  license: string
  as_of: string
  status: 'ok' | 'missing' | 'stale' | 'unsupported'
}
export interface AlertMessage {
  id: string
  authority: string
  status: 'active' | 'updated' | 'cancelled'
  event: string
  severity: Alert['severity']
  urgency: string
  certainty: string
  headline: string
  description: string
  instruction: string | null
  language: string
  issued_at: string | null
  effective_at: string | null
  onset_at: string | null
  expires_at: string | null
  ends_at: string | null
  area_description: string
  replaces: readonly string[]
  url: string | null
}
export type AlertLifecycle = 'active' | 'upcoming' | 'expired' | 'replaced' | 'cancelled' | 'unknown'
export interface AlertItem { message: AlertMessage; lifecycle: AlertLifecycle; category: Alert['category'] }
export type FeedState = 'fresh' | 'stale' | 'unknown' | 'unavailable' | 'unsupported' | 'checking' | 'demo'
export interface AlertView {
  state: FeedState
  label: string
  checkedAt: string | null
  active: readonly AlertItem[]
  upcoming: readonly AlertItem[]
  history: readonly AlertItem[]
  sources: readonly AlertSource[]
  /** An exact lifecycle/freshness boundary, never a polling interval. */
  nextChange: number | null
}

export const ALERT_SNAPSHOT_MAX_AGE = 5 * 60_000
export const instant = (value: string | null | undefined): number | null => {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) ? parsed : null
}
const rank: Record<Alert['severity'], number> = { Extreme: 0, Severe: 1, Moderate: 2, Minor: 3, Unknown: 4 }
export function alertCategory(event: string): Alert['category'] {
  return /warning|emergency/i.test(event) ? 'warning' : /watch/i.test(event) ? 'watch' : /advisory|alert/i.test(event) ? 'advisory' : /statement/i.test(event) ? 'statement' : 'other'
}
export function fromReportAlert(alert: Alert): AlertMessage {
  return {
    id: alert.id, authority: alert.sender, status: 'active', event: alert.event, severity: alert.severity,
    urgency: alert.urgency, certainty: alert.certainty, headline: alert.headline, description: alert.description,
    instruction: alert.instruction, language: 'en-US', issued_at: null, effective_at: alert.effective,
    onset_at: alert.onset, expires_at: alert.expires, ends_at: alert.ends, area_description: alert.area,
    replaces: [], url: alert.url,
  }
}
function reportSource(source: SourceStatus): AlertSource {
  return {
    id: source.id, kind: 'official', url: source.url, license: source.license,
    as_of: source.fetched_at ?? '',
    status: source.status === 'ok' ? 'ok' : source.status === 'skipped' ? 'unsupported' : 'missing',
  }
}
/** A v2 report timestamp is not evidence that the independently acquired alert feed succeeded. */
export function alertsFromReport(report: WeatherReport | undefined): AlertsFeed {
  const sources = report?.sources.filter(source => source.kind === 'alerts') ?? []
  const checked = sources.map(source => instant(source.fetched_at)).filter((time): time is number => time != null)
  const asOf = checked.length === sources.length && checked.length ? Math.min(...checked) : null
  const available = sources.length > 0 && sources.every(source => source.status === 'ok') && asOf != null
  return {
    alerts: report?.alerts.map(fromReportAlert) ?? [],
    freshness: {
      as_of: asOf == null ? '' : new Date(asOf).toISOString(),
      expires_at: available ? new Date(asOf + ALERT_SNAPSHOT_MAX_AGE).toISOString() : '',
      status: available ? 'available' : sources.length > 0 && sources.every(source => source.status === 'skipped') ? 'unsupported' : 'unavailable',
    },
    sources: sources.map(reportSource),
  }
}

export function buildAlertView(feed: AlertsFeed, now: number, options: { offline?: boolean; demo?: boolean; pending?: boolean } = {}): AlertView {
  const asOf = instant(feed.freshness.as_of), expires = instant(feed.freshness.expires_at)
  const clockKnown = Number.isFinite(now)
  const authoritative = feed.sources.length > 0 && feed.sources.every(source => source.kind === 'official' && source.status === 'ok' && instant(source.as_of) != null && instant(source.as_of)! <= now)
  let state: FeedState = options.pending ? 'checking' : feed.freshness.status === 'unsupported' ? 'unsupported' : feed.sources.length === 0 ? 'unknown' : feed.freshness.status === 'unavailable' ? 'unavailable' : 'unknown'
  if (!options.pending && feed.freshness.status === 'available' && asOf != null && expires != null && clockKnown && asOf <= now && expires > asOf) {
    state = options.offline || expires <= now || feed.sources.some(source => source.status === 'stale') ? 'stale' : authoritative ? 'fresh' : 'unknown'
  }
  if (options.offline && state === 'fresh') state = 'stale'
  if (options.demo) state = 'demo'

  // An update/cancellation supersedes references even when the old item has not reached its own expiry.
  const replaced = new Set(feed.alerts.filter(alert => (instant(alert.issued_at ?? alert.effective_at) ?? Infinity) <= now).flatMap(alert => [...alert.replaces]))
  const boundaries: number[] = []
  if (expires != null && expires > now) boundaries.push(expires)
  const items = feed.alerts.map((message): AlertItem => {
    const endTimes = [instant(message.expires_at), instant(message.ends_at)].filter((time): time is number => time != null)
    const end = endTimes.length ? Math.min(...endTimes) : null
    const effective = instant(message.effective_at)
    for (const time of [effective, instant(message.onset_at), end]) if (time != null && time > now) boundaries.push(time)
    const lifecycle: AlertLifecycle = message.status === 'cancelled' ? 'cancelled' : replaced.has(message.id) ? 'replaced' : end != null && end <= now ? 'expired' : !clockKnown || end == null || effective == null ? 'unknown' : effective > now ? 'upcoming' : 'active'
    return { message, lifecycle, category: alertCategory(message.event) }
  }).sort((a, b) => rank[a.message.severity] - rank[b.message.severity] || a.message.event.localeCompare(b.message.event) || a.message.id.localeCompare(b.message.id))
  const active = items.filter(item => item.lifecycle === 'active')
  const upcoming = items.filter(item => item.lifecycle === 'upcoming')
  const history = items.filter(item => item.lifecycle !== 'active' && item.lifecycle !== 'upcoming')
  const label: Record<FeedState, string> = {
    fresh: active.length ? `${active.length} active NWS ${active.length === 1 ? 'alert' : 'alerts'}` : 'No active NWS alerts',
    stale: 'Alert feed stale · status may have changed', unknown: 'Official alert status unknown',
    unavailable: 'Official alert feed unavailable', unsupported: 'Official alerts unsupported here',
    checking: 'Checking official alerts', demo: 'Demo alerts · not real warnings',
  }
  return { state, label: label[state], checkedAt: asOf == null ? null : feed.freshness.as_of, active, upcoming, history, sources: feed.sources, nextChange: boundaries.length ? Math.min(...boundaries) : null }
}

/** Only official weather.gov links are actionable; CAP message text is always rendered as text. */
export function officialUrl(value: string | null): string | null {
  if (!value) return null
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && (url.hostname === 'weather.gov' || url.hostname.endsWith('.weather.gov')) && !url.username && !url.password ? url.href : null
  } catch { return null }
}
